import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Button, Modal, Select } from "semantic-ui-react";
import { toast } from "../..";
import { addAuthorization } from "../../api/certificates";
import { TranslateProps } from "../../i18n/TranslateProps";
import {
    AuthorizationType,
    authorizationTypeOptions,
    IAuthorization,
    newAuthorizationInit,
} from "../../models/authorization";
import { ICertificate } from "../../models/certificate";
import { riverRangeProps } from "../../models/common";
import { ICrewMember } from "../../models/crewMember";
import { AppState } from "../../store/configureStore";

type Props = {
    open: boolean;
    close: () => void;
    activeAuthorizations: IAuthorization[];
    targetCertificate: ICertificate;
    crewMember: ICrewMember;
    fetchCrewMember: () => Promise<void>;
};

export const AddAuthorizationsForm = ({
    open,
    close,
    activeAuthorizations,
    targetCertificate,
    crewMember,
    fetchCrewMember,
}: Props) => {
    const user = useSelector((state: AppState) => state.user);
    const intl = useIntl();
    const [newAuth, setNewAuth] = useState(newAuthorizationInit);
    const [loading, setLoading] = useState(false);
    const availableAuths = authorizationTypeOptions.filter(
        x => !activeAuthorizations.some(a => a.type === x.value)
    );
    const availableRiverKilometers = riverRangeProps.filter(
        r =>
            !targetCertificate.riverKilometers.includes(r.value as number) &&
            !activeAuthorizations.flatMap(a => a.riverKilometers).includes(r.value as number)
    );
    if (
        availableRiverKilometers.length > 0 &&
        !availableAuths.some(a => a.value === AuthorizationType.S)
    )
        availableAuths.push({
            key: AuthorizationType.S,
            text: "sailingStretches",
            value: AuthorizationType.S,
            code: "S",
        });

    const handleAddAuthorization = async () => {
        setLoading(true);
        try {
            newAuth.linkedQualificationID = targetCertificate.qualificationID;
            newAuth.issuingAuthority = targetCertificate.issuingAuthority;
            newAuth.startDate = targetCertificate.startDate;
            newAuth.endDate = targetCertificate.endDate;
            newAuth.status = targetCertificate.status;
            newAuth.crewMemberGuid = crewMember.guid;
            await addAuthorization(newAuth);
            toast(intl.formatMessage({ id: "authorizationAddedMsg" }), true);
            fetchCrewMember();
        } catch {
            toast(intl.formatMessage({ id: "authorizationAddFail" }), false);
        } finally {
            setLoading(false);
            close();
        }
    };

    useEffect(() => {
        setNewAuth(a => ({ ...a, issuingAuthority: user.authority }));
    }, [user.authority]);

    return (
        <Modal
            open={open}
            onClose={() => {
                close();
                setNewAuth(newAuthorizationInit);
            }}
            dimmer="blurring"
            size="small"
            closeOnDimmerClick={!loading}
        >
            <Modal.Header>{intl.formatMessage({ id: "addAuthorization" })}</Modal.Header>
            <Modal.Content>
                <form className="add-authorization-form">
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "authorizationType" })}</label>
                        <Select
                            search
                            options={TranslateProps(availableAuths)}
                            value={newAuth.type}
                            onChange={(e, data: any) =>
                                setNewAuth(a => ({ ...a, type: +data.value }))
                            }
                        />
                    </section>
                    {newAuth.type === AuthorizationType.S && (
                        <section className="input-container">
                            <label>{intl.formatMessage({ id: "range" })}</label>
                            <Select
                                search
                                multiple
                                options={availableRiverKilometers}
                                value={newAuth.riverKilometers}
                                onChange={(e, data: any) =>
                                    setNewAuth({
                                        ...newAuth,
                                        riverKilometers: data.value,
                                    })
                                }
                            />
                        </section>
                    )}
                </form>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    primary
                    onClick={() => handleAddAuthorization()}
                    content={intl.formatMessage({ id: "add" })}
                    fluid
                    loading={loading}
                    disabled={loading || !targetCertificate || newAuth.type < 0}
                />
            </Modal.Actions>
        </Modal>
    );
};
